import {Divider, Slider, Space} from 'antd'
import {Rule} from 'antd/lib/form'

import styles from './ReviewForm.module.scss'
import useStore from 'base/store'
import {
  Box,
  Text,
  Button,
  Form,
  FormItem,
  useForm,
  useWatch,
  StarCheckIcon,
} from 'common/components'
import {fixNumber, totalNumberFromArray} from 'common/helpers'
import {useCreateProjectReview} from '..'

interface ReviewFormProps {
  projectUuid: string
  review: any
  onClose: () => void
}

const fieldRules: Rule[] = [
  () => ({
    validator(_: any, value) {
      if (
        _.field === 'onTimeRepaymentRating' ||
        _.field === 'onBudgetCompleteRating'
      )
        return Promise.resolve()
      return value ? Promise.resolve() : Promise.reject('')
    },
  }),
]

const countAverageRating = (formValues: any): number => {
  const values = Object.values(formValues)
  return fixNumber(totalNumberFromArray(values) / values.length)
}

const SliderInput = ({value, label, auto, ...props}: any) => (
  <div className={styles.input}>
    <div className={styles.label}>
      <Text weight="demiBold">{label}</Text>
      {auto && (
        <Text size="xs" color="grey" mt={2}>
          automatically rated
        </Text>
      )}
    </div>
    <div className={styles.slider}>
      <div className={styles.sliderLabels}>
        <span>highly dissatisfied</span>
        <span>highly satisfied</span>
      </div>
      <Slider
        {...props}
        value={value}
        tooltipVisible={false}
        max={10}
        disabled={auto}
      />
    </div>
    <div className={styles.points}>
      <Box align="center">
        <Text weight="medium" size="2xl" color={value ? undefined : 'grey'}>
          {value}
        </Text>
        <Text color="grey" size="xs">
          points
        </Text>
      </Box>
    </div>
  </div>
)

export default function ReviewForm({
  projectUuid,
  review,
  onClose,
}: ReviewFormProps) {
  const role = useStore((store) => store.role)
  const [form] = useForm()
  const formValues = useWatch(null, form)
  const {mutate: createProjectReview, isMutating} = useCreateProjectReview(
    projectUuid,
    onClose
  )

  const handleFinish = (values: any) => createProjectReview(values)

  const initialValues = {
    onBudgetCompleteRating: review.onBudgetCompleteRating,
    onTimeRepaymentRating: review.onTimeRepaymentRating,
    coworkRating: review.coworkRating,
    clientBackgroundRating: review.clientBackgroundRating,
    projectRating: review.projectRating,
    presalesRating: review.presalesRating,
    consentsRating: review.consentsRating,
    constructionRating: review.constructionRating,
  }

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={role.manager ? handleFinish : undefined}
    >
      <Box mb={72}>
        <FormItem name="onTimeRepaymentRating" rules={fieldRules}>
          <SliderInput auto label="On-budget complete" />
        </FormItem>
        <FormItem name="onBudgetCompleteRating" rules={fieldRules}>
          <SliderInput auto label="On-time repayment" />
        </FormItem>
      </Box>
      <Box mb={40}>
        <FormItem name="coworkRating" rules={fieldRules}>
          <SliderInput label="Co-work" />
        </FormItem>
        <FormItem name="clientBackgroundRating" rules={fieldRules}>
          <SliderInput label="Client background" />
        </FormItem>
        <FormItem name="projectRating" rules={fieldRules}>
          <SliderInput label="Project location, estimated profit, equity" />
        </FormItem>
        <FormItem name="presalesRating" rules={fieldRules}>
          <SliderInput label="Presales" />
        </FormItem>
        <FormItem name="consentsRating" rules={fieldRules}>
          <SliderInput label="Consents" />
        </FormItem>
        <FormItem name="constructionRating" rules={fieldRules}>
          <SliderInput label="Construction" />
        </FormItem>
      </Box>
      <Divider />
      <Box align="right" mt={40}>
        <Space size={32} align="center">
          {formValues && (
            <Box align="left">
              <Space size={6}>
                <StarCheckIcon width={22} height={22} />
                <Text as="span" size="3xl" weight="medium">
                  {countAverageRating(formValues)}
                </Text>
              </Space>
              <Text size="xs" weight="medium" color="grey">
                Average rating
              </Text>
            </Box>
          )}
          {role.manager && (
            <Button htmlType="submit" loading={isMutating}>
              Confirm review
            </Button>
          )}
        </Space>
      </Box>
    </Form>
  )
}
